import { Injectable } from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {classToPlain, plainToClass} from 'class-transformer';
import {db} from "../../dexie/db";

import { SOPutProcessRole } from './so-put-process-role';
import { SOGetProcessRole } from './so-get-process-role';
import { SOPostProcessRole } from './so-post-process-role';
import { SODeleteProcessRole } from './so-delete-process-role';
import {SORespProcessRole} from './so-resp-process-role';
import {SOPluralProcessRole} from './so-plural-process-role';

import {MMEntityService} from 'lionheartland';
import {EntityCoreProtocol} from 'lionheartland';
import {MMEntityServiceBase} from 'lionheartland';
import {HttpHeaders} from "@angular/common/http";

export class EOProcessRoleService extends MMEntityServiceBase implements MMEntityService {

  constructor(
    public entityCore: EntityCoreProtocol,
  ) {
    super('process_role', entityCore);
  }

  /** GET ProcessRole by id. Will 404 if id not found */
  get(request: SOGetProcessRole): Observable<SOPluralProcessRole> {
    return this.httpClient.get<SOPluralProcessRole>(this.makeGetUri(request), this.httpOptions).pipe(
      map(resp => plainToClass(SOPluralProcessRole, resp)),
      tap(async (response: SOPluralProcessRole) => {
        this.log(`fetched ProcessRole`);
        // After a successful server response, update IndexedDB
        const activities = response.toProcessRoles(); // Convert your response to an array of ProcessRole instances
        const promises = activities.map(processRole => db.processRoles.put(processRole));
        await Promise.all(promises); // Wait for all the IndexedDB update operations to complete
      }),
      catchError(this.handleError<SOPluralProcessRole>(`get ProcessRole`))
    );
  }

    /** GETCACHE for initial display with CacheQueryProtocol */
    getCache(cacheQuery: any): Observable<SOPluralProcessRole> {
        console.log("GETCACHE", cacheQuery);

        if (cacheQuery.filterKey === 'processRole' && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on parent and organization")
            return from(db.processRoles.where('parentUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(processRolesArray => this.processProcessRoles(processRolesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }
        else if (cacheQuery.filterKey + "s" == "process" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.processRoles.where('processUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(processRolesArray => this.processProcessRoles(processRolesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "role" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.processRoles.where('roleUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(processRolesArray => this.processProcessRoles(processRolesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "project" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.processRoles.where('projectUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(processRolesArray => this.processProcessRoles(processRolesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "organization" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.processRoles.where('organizationUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(processRolesArray => this.processProcessRoles(processRolesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterOrganization || cacheQuery.organization.uid)
        {
            console.log("tripped organization for cache query")
            if ( cacheQuery?.filterOrganization != null )
            {
                console.log("cache filtering based on organization CQP", cacheQuery)
                return from(db.processRoles.where('organizationUid').equals(cacheQuery.filterOrganization).toArray()).pipe(
                    map(processRolesArray => this.processProcessRoles(processRolesArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else if ( cacheQuery?.organization?.uid != null )
            {
                console.log("UID:", cacheQuery.organization.uid)
                console.log("cache filtering based on organization SOGet")
                return from(db.processRoles.where('organizationUid').equals(cacheQuery.organization.uid).toArray()).pipe(
                    map(processRolesArray => this.processProcessRoles(processRolesArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else
            {
                console.log("null organization")
            }
        }
        else
        {
            console.log("cache no filter")
            return from(db.processRoles.toArray()).pipe(
                map((processRolesArray) => {
                    // Convert the array of plain objects to an array of SORespProcessRole instances
                    const sorProcessRoles = processRolesArray.map(req => plainToClass(SORespProcessRole, req));
                    // Create and return a new instance of SOPluralProcessRole
                    const pluralProcessRole = new SOPluralProcessRole();
                    pluralProcessRole.objects = sorProcessRoles;
                    pluralProcessRole.nextOffset = null; // Assuming there is no pagination in cache
                    return pluralProcessRole;
                }),
                catchError(error => this.handleQueryError(error))
            );
        }
    }

    private processProcessRoles(processRolesArray) {
        const sorProcessRoles = processRolesArray.map(req => plainToClass(SORespProcessRole, req));
        const pluralProcessRole = new SOPluralProcessRole();
        pluralProcessRole.objects = sorProcessRoles;
        pluralProcessRole.nextOffset = null;
        return pluralProcessRole;
    }

    private handleQueryError(error) {
        console.error('Error in query:', error);
        return throwError(error); // Return an observable error
    }

post(request: SOPostProcessRole): Observable<SORespProcessRole> {
  return this.httpClient.post<SORespProcessRole>(this.coreUrl + this.route, request, this.httpOptions).pipe(
    map(resp => plainToClass(SORespProcessRole, resp)),

    tap(async (response: SORespProcessRole) => {
      this.log(`added ProcessRole w/ id=${response.uid}`);
      // After a successful server response, update IndexedDB
      console.log("here", response);
      console.log("here request", request);
      await db.processRoles.add(response.toProcessRole());
    }),
    catchError(this.handleError<SORespProcessRole>('ProcessRole post'))
  );
}


  put(request: SOPutProcessRole): Observable<SORespProcessRole> {
    const uid = request.uid;
    const uri = `${this.coreUrl}${this.route}/${uid}`;
    return this.httpClient.put<SORespProcessRole>(uri, request, this.httpOptions).pipe(
      map(resp => plainToClass(SORespProcessRole, resp)),
      tap(async (response: SORespProcessRole) => {
        this.log(`edited ProcessRole w/ uid=${uid}`);
        // After a successful server response, update IndexedDB
        await db.processRoles.put(response.toProcessRole());
      }),
      catchError(this.handleError<SORespProcessRole>('ProcessRole put'))
    );
  }


  delete(request: SODeleteProcessRole): Observable<SORespProcessRole> {
    return this.httpClient.delete<SORespProcessRole>(this.makeDeleteUri(request), this.httpOptions).pipe(
      tap(async _ => {
        this.log(`deleted ProcessRole uid=${request.uid}`);
        // After a successful server response, update IndexedDB
        await db.processRoles.delete(request.uid);
      }),
      catchError(this.handleError<SORespProcessRole>('ProcessRole delete'))
    );
  }


}
